import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import AgentAccountInfo from "../../components/profile/AgentAccountInfo";
import AgentTransferPoints from "../../components/profile/AgentTransferPoints";
// import AgentSettings from "../../components/profile/AgentSettings";
import AgencySearchModal from "../../components/agency/AgencySearchModal";
import { userService, agentService } from "../../services/api";
import toast from "react-hot-toast";

const AgentProfile = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Get active tab from URL or default to account-info for agents
  const activeTab = searchParams.get("tab") || "account-info";

  // Agent Profile API state
  const [agentProfileData, setAgentProfileData] = useState(null);
  const [formApiData, setFormApiData] = useState({
    name: "",
    mobile: "",
    email: "",
    agency: null,
    position: "",
    license_number: "",
    license_organization_name: "",
    document_url: null,
    passport_number: "",
    address: "",
    city: "",
    country: "",
    zipcode: "",
    profile_image: null,
    agency_logo: null,
    account_id: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  // Agency search modal state
  const [showAgencySearchModal, setShowAgencySearchModal] = useState(false);

  // Map API data to form fields
  const deriveAgentName = (data) => {
    if (!data) return "";
    if (data.name) return data.name;
    const parts = [data.first_name, data.last_name].filter(Boolean);
    return parts.join(" ").trim();
  };

  const mapApiToForm = (data) => ({
    name: deriveAgentName(data),
    mobile: data.mobile || "",
    email: data.email || "",
    agency: data.agency || null,
    position: data.position || "",
    license_number: data.license_number || "",
    license_organization_name: data.license_organization_name || "",
    document_url: data.document_url || null,
    passport_number: data.passport_number || "",
    address: data.address || "",
    city: data.city || "",
    country: data.country || "",
    zipcode: data.zipcode || "",
    profile_image: data.profile_image || null,
    agency_logo: data.agency_logo || null,
    account_id: data.account_id || "",
  });

  // Map form fields to API payload
  const mapFormToApi = (form) => {
    // Create FormData for multipart/form-data submission
    const formData = new FormData();

    // Append text fields (only if they have values)
    if (form.name) formData.append("name", form.name);
    if (form.mobile) formData.append("mobile", form.mobile);
    if (form.position) formData.append("position", form.position);
    if (form.license_number)
      formData.append("license_number", form.license_number);
    if (form.license_organization_name)
      formData.append(
        "license_organization_name",
        form.license_organization_name
      );
    if (form.passport_number)
      formData.append("passport_number", form.passport_number);
    if (form.address) formData.append("address", form.address);
    if (form.city) formData.append("city", form.city);
    if (form.country) formData.append("country", form.country);
    if (form.zipcode) formData.append("zipcode", form.zipcode);
    if (form.profile_image)
      formData.append("profile_picture", form.profile_image);
    if (form.agency_logo) formData.append("agency_logo", form.agency_logo);

    // Only append document if it's a File object (new upload)
    if (form.document instanceof File) {
      formData.append("document", form.document);
    }

    return formData;
  };

  // Fetch agent profile on mount
  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setError("");

    userService
      .getAgentProfile()
      .then((result) => {
        if (isMounted) {
          if (result.success) {
            setAgentProfileData(result.data);
            console.log("agent profile", result.data);
            setFormApiData(mapApiToForm(result.data));
          } else {
            setError(result.error);
          }
        }
      })
      .catch((err) => {
        console.log(err);
        if (isMounted) {
          setError("Failed to load profile");
        }
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  // Handle API-editable field changes
  const handleApiInputChange = (field, value) => {
    setFormApiData((prev) => ({ ...prev, [field]: value }));
  };

  // Handle file upload for document
  const handleDocumentChange = (file) => {
    setFormApiData((prev) => ({ ...prev, document: file }));
  };

  // Handle agency selection from search modal
  const handleAgencySelect = async (agency) => {
    try {
      const response = await agentService.createJoinRequest(agency.id);
      if (response.success) {
        toast.success("Request sent to agency admin successfully");
        // Update the form data to show the selected agency
        setFormApiData((prev) => ({ ...prev, agency: agency }));
      } else {
        toast.error(response.error || "Failed to send request");
      }
    } catch (error) {
      console.error("Error sending agency join request:", error);
      toast.error("Failed to send request. Please try again.");
    }
  };

  const handleMobileUpdate = (newMobile) => {
    setFormApiData((prev) => ({ ...prev, mobile: newMobile }));
    setAgentProfileData((prev) =>
      prev ? { ...prev, mobile: newMobile } : prev
    );
  };

  // Save changes to API
  const handleSave = async () => {
    setIsSaving(true);
    setError("");

    try {
      console.log("formApiData", formApiData);
      const payload = mapFormToApi(formApiData);
      const result = await userService.updateAgentProfile(payload);

      if (result.success) {
        setAgentProfileData(result.data);
        setFormApiData(mapApiToForm(result.data));
        setIsEditing(false);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError("Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  };

  // Cancel editing and revert changes
  const handleCancel = () => {
    if (agentProfileData) {
      setFormApiData(mapApiToForm(agentProfileData));
    }
    setIsEditing(false);
    setError("");
  };

  // Update URL when tab changes
  const setActiveTab = (tab) => {
    setSearchParams({ tab });
  };

  const tabs = [
    { id: "account-info", label: "Account Info" },
    { id: "my-transferpoints", label: "My TransferPoints" },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case "account-info":
        return (
          <AgentAccountInfo
            agentProfileData={agentProfileData}
            formApiData={formApiData}
            isEditing={isEditing}
            isLoading={isLoading}
            isSaving={isSaving}
            error={error}
            onApiInputChange={handleApiInputChange}
            onDocumentChange={handleDocumentChange}
            onSave={handleSave}
            onCancel={handleCancel}
            onEdit={() => setIsEditing(true)}
            onAgencySearch={() => setShowAgencySearchModal(true)}
            onMobileUpdate={handleMobileUpdate}
          />
        );
      case "my-transferpoints":
        return <AgentTransferPoints />;
      default:
        return (
          <AgentAccountInfo
            agentProfileData={agentProfileData}
            formApiData={formApiData}
            isEditing={isEditing}
            isLoading={isLoading}
            isSaving={isSaving}
            error={error}
            onApiInputChange={handleApiInputChange}
            onDocumentChange={handleDocumentChange}
            onSave={handleSave}
            onCancel={handleCancel}
            onEdit={() => setIsEditing(true)}
            onAgencySearch={() => setShowAgencySearchModal(true)}
            onMobileUpdate={handleMobileUpdate}
          />
        );
    }
  };

  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      <div className="mx-auto">
        {/* Header */}
        <div className="bg-white py-4 border-b border-[#E5E6E8]">
          <div className="px-4 md:px-14 flex items-center justify-between">
            <h1 className="text-[20px] font-bold text-gray-900">My Profile</h1>
            <button onClick={() => navigate(`/search/agent/${formApiData.account_id}`)} className="bg-[#2283FF] px-4 py-2 text-[#F5F5F5] rounded-lg">
              Preview public profile
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        {isMobile ? (
          <div className="px-4 bg-white flex justify-between items-center border-y border-[#0000001A] overflow-x-auto">
            <div className="flex items-center gap-4">
              {tabs.map((tab) => (
                <div
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 text-xs whitespace-nowrap cursor-pointer ${
                    activeTab === tab.id
                      ? "border-b-2 border-[#2283FF] text-[#2283FF] font-medium"
                      : "text-[#1E1E1ECC]"
                  }`}
                >
                  {tab.label}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-white py-4 flex items-center">
            <div className="sm:px-14 px-4">
              <nav className="flex space-x-8">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-2 px-4 font-medium text-sm rounded-lg transition-colors ${
                      activeTab === tab.id
                        ? "bg-[#F3F4F6] text-[#0B0B0B]"
                        : "text-[#1E1E1ECC] hover:text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>
        )}

        {/* Tab Content */}
        <div className="bg-[#F8F9FB] rounded-lg shadow-sm">
          {renderTabContent()}
        </div>
      </div>

      {/* Agency Search Modal */}
      <AgencySearchModal
        isOpen={showAgencySearchModal}
        onClose={() => setShowAgencySearchModal(false)}
        onAgencySelect={handleAgencySelect}
        title="Search Agencies to Join"
      />
    </div>
  );
};

export default AgentProfile;
